"use client"

import {
  Button,
  Field,
  HStack,
  Icon,
  Input,
  Spinner,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import {
  EmailOutlineIcon,
  PersonOutlineIcon,
  SendIcon,
} from "@/components/icons"
import React, { memo, useCallback, useEffect, useReducer, useRef } from "react"

import Confetti from "@/components/Confetti"
import ErrorMessage from "@/components/ErrorMessage"
import { InputGroup } from "@/components/ui/input-group"
import { sendContactForm } from "@/lib/api"
import { useForm } from "react-hook-form"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useLocalStorage } from "@/hooks/useLocalStorage"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"

type FormValues = {
  name: string
  email: string
  message: string
}

const defaultValues = {
  email: "",
  message: "",
  name: "",
}

// Zod validation schema
const validationSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Your email is required" })
    .email({ message: "Invalid email address" }),
  message: z.string().min(1, { message: "Your message is required" }),
  name: z.string().min(1, { message: "Your name is required" }),
})

interface FormStatusState {
  validatingCaptcha: boolean
  isConfettiVisible: boolean
  feedbackTypeMsg: string
}

type FormStatusAction =
  | { type: "start-captcha" }
  | { type: "submit-success" }
  | { type: "set-feedback"; feedback: string }
  | { type: "reset-feedback" }

function formStatusReducer(
  state: FormStatusState,
  action: FormStatusAction
): FormStatusState {
  switch (action.type) {
    case "start-captcha":
      return { ...state, validatingCaptcha: true }
    case "submit-success":
      return {
        feedbackTypeMsg: "sent",
        isConfettiVisible: true,
        validatingCaptcha: false,
      }
    case "set-feedback":
      return {
        ...state,
        feedbackTypeMsg: action.feedback,
        validatingCaptcha: false,
      }
    case "reset-feedback":
      return { ...state, feedbackTypeMsg: "", isConfettiVisible: false }
    default:
      return state
  }
}

const formStatusInitialState: FormStatusState = {
  feedbackTypeMsg: "",
  isConfettiVisible: false,
  validatingCaptcha: false,
}

const ContactFormContainer: React.FC = memo(() => {
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    mode: "onChange",
    resolver: zodResolver(validationSchema),
  })

  const { executeRecaptcha } = useGoogleReCaptcha()
  const [formStatus, dispatchFormStatus] = useReducer(
    formStatusReducer,
    formStatusInitialState
  )
  const { feedbackTypeMsg, isConfettiVisible, validatingCaptcha } = formStatus
  const [storage, setStorage] = useLocalStorage(
    "contactFormValues",
    defaultValues
  )
  const formData = watch()
  const prevFormDataRef = useRef<FormValues>(defaultValues)
  const isInitialMount = useRef(true)

  // Load stored form data only on initial mount
  useEffect(() => {
    if (isInitialMount.current && storage && Object.keys(storage).length > 0) {
      reset(storage)
      isInitialMount.current = false
    }
  }, [storage, reset])

  // Handle form value changes without affecting error state
  const handleFormChange = useCallback(
    (values: FormValues) => {
      // Skip if this is the initial mount
      if (isInitialMount.current) {
        return
      }

      const hasFormData = Object.values(values).some(
        (value) => value.trim() !== ""
      )

      if (!hasFormData) {
        return
      }

      const hasChanged = Object.keys(values).some(
        (key) =>
          values[key as keyof FormValues] !==
          prevFormDataRef.current[key as keyof FormValues]
      )

      if (!hasChanged) {
        return
      }

      const formValues = {
        email: values.email,
        message: values.message,
        name: values.name,
      }
      setStorage(formValues)
      prevFormDataRef.current = { ...values }
    },
    [setStorage]
  )

  // Watch form changes and update storage
  useEffect(() => {
    handleFormChange(formData)
  }, [formData, handleFormChange])

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!executeRecaptcha) {
        console.error("reCAPTCHA not initialized")
        dispatchFormStatus({ feedback: "captcha", type: "set-feedback" })
        return
      }

      try {
        dispatchFormStatus({ type: "start-captcha" })
        const gReCaptchaToken = await executeRecaptcha("enquiryFormSubmit")

        const result = await sendContactForm(values, gReCaptchaToken)

        if ((result as { success: boolean }).success) {
          dispatchFormStatus({ type: "submit-success" })
          setStorage(defaultValues)
          reset()
        } else {
          dispatchFormStatus({
            feedback:
              result.message === "Captcha validation failed"
                ? "captcha"
                : "error",
            type: "set-feedback",
          })
        }
      } catch (error) {
        console.error("Error submitting form:", error)
        dispatchFormStatus({ feedback: "error", type: "set-feedback" })
      }
    },
    [executeRecaptcha, setStorage, reset]
  )

  // Handle timeout cleanup
  useEffect(() => {
    if (feedbackTypeMsg !== "sent") {
      return
    }

    const timeoutId = window.setTimeout(() => {
      dispatchFormStatus({ type: "reset-feedback" })
    }, 50000)

    return () => window.clearTimeout(timeoutId)
  }, [feedbackTypeMsg])

  return (
    <VStack
      alignSelf="auto"
      flex="1 0 auto"
      flexBasis="40%"
      gap={5}
      order={1}
      position="relative"
    >
      <VStack as="form" gap={5} onSubmit={handleSubmit(onSubmit)} width="100%">
        <Field.Root id="name" invalid={!!errors.name} required>
          <Field.Label>
            Your Name: <Field.RequiredIndicator />
          </Field.Label>
          <InputGroup
            borderColor="gray.300"
            startElement={
              <Icon as={PersonOutlineIcon} size="md" color="fg.muted" />
            }
            width="100%"
          >
            <Input
              _dark={{
                bgColor: "gray.900",
                borderColor: "gray.700",
              }}
              borderRadius="sm"
              placeholder="Type your Name"
              size="md"
              colorPalette="primary"
              type="text"
              {...register("name")}
            />
          </InputGroup>
          <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
        </Field.Root>

        <Field.Root id="email" invalid={!!errors.email} required>
          <Field.Label>
            Your Email: <Field.RequiredIndicator />
          </Field.Label>
          <InputGroup
            borderColor="gray.300"
            startElement={
              <Icon as={EmailOutlineIcon} size="sm" color="fg.muted" />
            }
            width="100%"
          >
            <Input
              _dark={{
                bgColor: "gray.900",
                borderColor: "gray.700",
              }}
              borderRadius="sm"
              placeholder="Type your Email Address"
              size="md"
              colorPalette="primary"
              type="email"
              {...register("email")}
            />
          </InputGroup>
          <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
        </Field.Root>

        <Field.Root id="message" invalid={!!errors.message} required>
          <Field.Label>
            Message: <Field.RequiredIndicator />
          </Field.Label>
          <Textarea
            _dark={{
              bgColor: "gray.900",
              borderColor: "gray.700",
            }}
            borderColor="gray.300"
            borderRadius="sm"
            height="120px"
            placeholder="Type your message..."
            resize="vertical"
            variant="subtle"
            colorPalette="primary"
            {...register("message")}
          />
          <Field.ErrorText>{errors.message?.message}</Field.ErrorText>
        </Field.Root>

        <HStack width="100%">
          {feedbackTypeMsg === "error" && (
            <ErrorMessage
              message="Please try again. Don't worry, you can refresh without losing your form data.\n Please contact me through hello@gilalvaro.com."
              title="Apologies there was an error sending the message."
            />
          )}
          {feedbackTypeMsg === "sent" && (
            <ErrorMessage
              message="I'll get back to you as soon as possible."
              status="success"
              title="Message sent successfully!"
            />
          )}
          {feedbackTypeMsg === "captcha" && (
            <ErrorMessage
              message="Please try again or contact me through social media."
              title="reCAPTCHA validation failed"
            />
          )}
        </HStack>

        <Button
          colorPalette="primary"
          loading={isSubmitting || validatingCaptcha}
          loadingText={validatingCaptcha ? "Validating..." : "Sending..."}
          rounded="full"
          size="lg"
          spinner={<Spinner size="sm" />}
          type="submit"
          width="100%"
        >
          <SendIcon />
          Send Message
        </Button>
      </VStack>

      {isConfettiVisible && <Confetti />}
    </VStack>
  )
})

ContactFormContainer.displayName = "ContactFormContainer"

export default ContactFormContainer
